import { getLoansByFarmer, getDebtsByFarmer } from './loans';
import { getRawLimitByFarmer } from './limits';

export interface FarmerMoneySummary {
  hanMucTienTong: number;
  /** used + locked combined — "hạn mức đã tiêu hao" for the Còn khả dụng /
   * progress-bar math below. Never render this next to a separate "Đang khóa"
   * tile: it already contains that amount, which is exactly Feedback 08 A1
   * ("Đã sử dụng" == "Đang khóa" — a farmer with 0 actually disbursed and
   * 60tr locked saw 60tr in both tiles). Use `hanMucTienDaSuDungThucTe` for
   * any display shown alongside a locked-amount tile. */
  hanMucTienDaDung: number;
  /** `usedAmountVnd` alone — money actually disbursed/consumed, excluding
   * whatever is merely locked pending a decision. This is what a tile
   * literally labeled "Đã sử dụng"/"Đã dùng" should show. */
  hanMucTienDaSuDungThucTe: number;
  hanMucTienKhaDung: number;
  hanMucTienDangKhoa: number;
  lockedAmount: number;
  duNo: number;
  choGiaiNgan: number;
  choKiemTra: number;
  daDuyet: number;
  quaHan: number;
  sapDenHan: number;
}

const DUE_SOON_DAYS = 7;

/** Real replacement for the deleted `financeEngine.ts`'s `getFarmerMoneySummary` —
 * covers only the fields the surviving pages actually read (checked against every
 * consumer before writing this), computed from the real Limit record + derived Debt
 * view instead of the old mock's lock/HTX-source simulation. */
export function getFarmerMoneySummary(farmerId?: string): FarmerMoneySummary {
  const limit = getRawLimitByFarmer(farmerId);
  const loans = getLoansByFarmer(farmerId);
  const debts = getDebtsByFarmer(farmerId);

  const hanMucTienTong = limit?.totalLimitVnd || 0;
  const hanMucTienDangKhoa = limit?.lockedAmountVnd || 0;
  const hanMucTienDaSuDungThucTe = limit?.usedAmountVnd || 0;
  const hanMucTienDaDung = hanMucTienDaSuDungThucTe + hanMucTienDangKhoa;
  const hanMucTienKhaDung = Math.max(0, hanMucTienTong - hanMucTienDaDung);

  const duNo = debts.filter((d) => d.status !== 'Đã tất toán').reduce((sum, d) => sum + d.conNo, 0);
  const quaHan = debts.filter((d) => d.status === 'Quá hạn').length;
  const soonThreshold = new Date();
  soonThreshold.setDate(soonThreshold.getDate() + DUE_SOON_DAYS);
  const sapDenHan = debts.filter(
    (d) => d.status === 'Đang vay' && d.ngayDenHan && new Date(d.ngayDenHan) <= soonThreshold,
  ).length;

  const moneyLoans = loans.filter((l) => l.loai !== 'Vật tư');
  const choKiemTra = moneyLoans.filter((l) => l.status === 'Chờ kiểm tra').length;
  const daDuyet = moneyLoans.filter((l) => l.status === 'Đã duyệt').length;
  const choGiaiNgan = daDuyet;

  return {
    hanMucTienTong,
    hanMucTienDaDung,
    hanMucTienDaSuDungThucTe,
    hanMucTienKhaDung,
    hanMucTienDangKhoa,
    lockedAmount: hanMucTienDangKhoa,
    duNo,
    choGiaiNgan,
    choKiemTra,
    daDuyet,
    quaHan,
    sapDenHan,
  };
}

export function getFarmerRemainingLimit(farmerId?: string): number {
  return getFarmerMoneySummary(farmerId).hanMucTienKhaDung;
}

export function getFarmerTotalDebt(farmerId?: string): number {
  return getFarmerMoneySummary(farmerId).duNo;
}

export function getFarmerOverdueDebt(farmerId?: string): number {
  return getDebtsByFarmer(farmerId)
    .filter((d) => d.status === 'Quá hạn')
    .reduce((sum, d) => sum + d.conNo, 0);
}

export function getFarmerOverdueCount(farmerId?: string): number {
  return getFarmerMoneySummary(farmerId).quaHan;
}
